import { useMemo, useState } from 'react';
import { fmtMoney, fmtMoney0, fmtDate, toAUD, statusPill, fulfilmentPill, INVOICE_STATUSES } from './b2bLib';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const SORTS = {
  recent: { label: 'Newest first', get: i => i.issue_date || '', dir: -1 },
  due: { label: 'Due date', get: i => i.due_date || '9999', dir: 1 },
  balance: { label: 'Balance', get: i => Number(i.balance || 0), dir: -1 },
  days_overdue: { label: 'Days overdue', get: i => Number(i.days_overdue || 0), dir: -1 },
};

const TH = { textAlign: 'left', fontSize: 10, fontWeight: 500, color: '#a1a1aa', padding: '8px 10px', borderBottom: '1px solid #f4f4f5', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' };
const TD = { padding: '9px 10px', borderBottom: '1px solid #fafafa', whiteSpace: 'nowrap' };
const RIGHT = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function Pill({ p }) {
  return <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: p.bg, color: p.fg }}>{p.label || ''}</span>;
}

export default function Receivables({ session, invoices, customers, fx, trackingBy, onOpenInvoice }) {
  // No finer-grained "b2b_write" flag exists yet — admin is the closest
  // write-permission proxy for this read-only dashboard.
  const canWrite = session?.role === 'admin';
  const [fStatus, setFStatus] = useState('');
  const [fCustomer, setFCustomer] = useState('');
  const [fRegion, setFRegion] = useState('');
  const [fCurrency, setFCurrency] = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState('recent');
  const [groupBy, setGroupBy] = useState('none'); // none | status | customer

  const regions = useMemo(() => [...new Set(invoices.map(i => i.customer_country).filter(Boolean))].sort(), [invoices]);

  const rows = useMemo(() => {
    let r = invoices.slice();
    if (fStatus) r = r.filter(i => i.status === fStatus);
    if (fCustomer) r = r.filter(i => i.customer_name === fCustomer);
    if (fRegion) r = r.filter(i => i.customer_country === fRegion);
    if (fCurrency) r = r.filter(i => i.currency === fCurrency);
    if (overdueOnly) r = r.filter(i => i.is_overdue);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      r = r.filter(i => [i.customer_name, i.invoice_ref, i.po_ref, i.sales_order_ref].some(v => (v || '').toLowerCase().includes(s)));
    }
    const sc = SORTS[sortKey];
    r.sort((a, b) => { const av = sc.get(a), bv = sc.get(b); return av < bv ? -sc.dir : av > bv ? sc.dir : 0; });
    return r;
  }, [invoices, fStatus, fCustomer, fRegion, fCurrency, overdueOnly, q, sortKey]);

  const totals = useMemo(() => {
    let outstanding = 0, count = rows.length;
    rows.forEach(i => { if (['Issued', 'Part-Paid'].includes(i.status)) outstanding += toAUD(i.balance, i.currency, fx); });
    return { outstanding, count };
  }, [rows, fx]);

  // grouping
  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: null, rows }];
    const m = new Map();
    rows.forEach(i => {
      const k = groupBy === 'status' ? (i.is_overdue ? 'Overdue' : i.status) : i.customer_name;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(i);
    });
    return [...m.entries()].map(([key, rs]) => ({ key, rows: rs }))
      .sort((a, b) => b.rows.length - a.rows.length);
  }, [rows, groupBy]);

  const selStyle = { fontSize: 12, padding: '6px 8px', border: '1px solid #e4e4e7', borderRadius: 8, background: '#fff', color: '#0a0a0a' };

  function handleDownload() {
    const columns = [
      { label: 'Customer', width: 24, value: r => r.customer_name || '' },
      { label: 'Invoice', width: 16, value: r => r.invoice_ref || '' },
      { label: 'PO', width: 14, value: r => r.po_ref || '' },
      { label: 'Currency', width: 10, value: r => r.currency || '' },
      { label: 'Amount', width: 14, numFmt: '#,##0.00', value: r => Number(r.amount_inc_gst || 0) },
      { label: 'Paid', width: 14, numFmt: '#,##0.00', value: r => Number(r.paid_to_date || 0) },
      { label: 'Balance', width: 14, numFmt: '#,##0.00', value: r => Number(r.balance || 0) },
      { label: 'Status', width: 14, value: r => r.status || '' },
      { label: 'Terms', width: 12, value: r => r.terms || '' },
      { label: 'Issued', width: 14, value: r => r.issue_date || '' },
      { label: 'Due', width: 14, value: r => r.due_date || '' },
      { label: 'Days Overdue', width: 14, value: r => r.days_overdue || '' },
      { label: 'Fulfilment', width: 14, value: r => r.fulfilment_status || '' },
    ];
    const filename = `FON_B2B_Receivables_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Receivables', columns, rows);
    logExportActivity(session, 'B2B Receivables', filename);
  }

  return (
    <div>
      {/* filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customer / ref…"
          style={{ ...selStyle, minWidth: 200, flex: '0 0 auto' }} />
        <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={selStyle}>
          <option value="">All statuses</option>
          {INVOICE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={fCustomer} onChange={e => setFCustomer(e.target.value)} style={selStyle}>
          <option value="">All customers</option>
          {customers.map(c => <option key={c.customer_id} value={c.name}>{c.name}</option>)}
        </select>
        <select value={fRegion} onChange={e => setFRegion(e.target.value)} style={selStyle}>
          <option value="">All regions</option>
          {regions.map(rg => <option key={rg} value={rg}>{rg}</option>)}
        </select>
        <select value={fCurrency} onChange={e => setFCurrency(e.target.value)} style={selStyle}>
          <option value="">All currencies</option>
          {['AUD', 'NZD', 'SGD'].map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label style={{ fontSize: 12, color: '#52525b', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          <input type="checkbox" checked={overdueOnly} onChange={e => setOverdueOnly(e.target.checked)} /> Overdue only
        </label>
        <div style={{ flex: 1 }} />
        {canWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
        <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={selStyle} title="Sort">
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>Sort: {v.label}</option>)}
        </select>
        <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={selStyle} title="Group">
          <option value="none">No grouping</option>
          <option value="status">Group: Status</option>
          <option value="customer">Group: Customer</option>
        </select>
      </div>

      <div style={{ fontSize: 11, color: '#8B9094', marginBottom: 8 }}>
        {totals.count} invoice{totals.count === 1 ? '' : 's'} · {fmtMoney0(totals.outstanding)} outstanding (AUD)
      </div>

      <div style={{ background: '#fff', border: '1px solid #e4e4e7', borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1100 }}>
          <thead>
            <tr>
              <th style={TH}>Customer</th>
              <th style={TH}>Invoice</th>
              <th style={TH}>PO</th>
              <th style={{ ...TH, ...RIGHT }}>Amount</th>
              <th style={{ ...TH, ...RIGHT }}>Paid</th>
              <th style={{ ...TH, ...RIGHT }}>Balance</th>
              <th style={TH}>Status</th>
              <th style={TH}>Terms</th>
              <th style={TH}>Issued</th>
              <th style={TH}>Due</th>
              <th style={{ ...TH, ...RIGHT }}>Overdue</th>
              <th style={TH}>Fulfilment</th>
              <th style={TH}>Skutopia</th>
              <th style={TH}>Tracking</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={14} style={{ padding: 28, textAlign: 'center', color: '#a1a1aa' }}>No invoices match.</td></tr>
            )}
            {groups.map(g => (
              <GroupRows key={g.key ?? '_all'} g={g} groupBy={groupBy} fx={fx} trackingBy={trackingBy} onOpenInvoice={onOpenInvoice} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GroupRows({ g, groupBy, fx, trackingBy, onOpenInvoice }) {
  const sub = g.rows.reduce((s, i) => s + (['Issued', 'Part-Paid'].includes(i.status) ? toAUD(i.balance, i.currency, fx) : 0), 0);
  return (
    <>
      {groupBy !== 'none' && (
        <tr style={{ background: '#fafafa' }}>
          <td colSpan={14} style={{ padding: '7px 10px', fontSize: 11, fontWeight: 700, color: '#52525b' }}>
            {g.key || '—'} <span style={{ color: '#a1a1aa', fontWeight: 500 }}>· {g.rows.length} · {fmtMoney0(sub)} outstanding</span>
          </td>
        </tr>
      )}
      {g.rows.map(i => {
        const trk = trackingBy[i.id] || [];
        const sp = statusPill(i.status, i.is_overdue);
        const fp = fulfilmentPill(i.fulfilment_status);
        return (
          <tr key={i.id} onClick={() => onOpenInvoice(i.id)} style={{ cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <td style={{ ...TD, fontWeight: 500, color: '#0a0a0a' }}>{i.customer_name}</td>
            <td style={{ ...TD, color: '#52525b' }}>{i.invoice_ref || '—'}</td>
            <td style={{ ...TD, color: '#52525b' }}>{i.po_ref || '—'}</td>
            <td style={{ ...TD, ...RIGHT }}>{i.amount_inc_gst == null ? '—' : fmtMoney(i.amount_inc_gst, i.currency)}</td>
            <td style={{ ...TD, ...RIGHT, color: '#16a34a' }}>{Number(i.paid_to_date) ? fmtMoney(i.paid_to_date, i.currency) : '—'}</td>
            <td style={{ ...TD, ...RIGHT, fontWeight: 600 }}>{fmtMoney(i.balance, i.currency)}</td>
            <td style={TD}><Pill p={sp} /></td>
            <td style={{ ...TD, color: '#52525b', fontSize: 11 }}>{i.terms || '—'}</td>
            <td style={{ ...TD, color: '#52525b' }}>{fmtDate(i.issue_date)}</td>
            <td style={{ ...TD, color: i.is_overdue ? '#dc2626' : '#52525b' }}>{fmtDate(i.due_date)}</td>
            <td style={{ ...TD, ...RIGHT, color: i.days_overdue ? '#dc2626' : '#d4d4d8', fontWeight: i.days_overdue ? 600 : 400 }}>{i.days_overdue || '—'}</td>
            <td style={TD}><span style={{ fontSize: 9, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: fp.bg, color: fp.fg }}>{i.fulfilment_status}</span></td>
            <td style={TD} onClick={e => e.stopPropagation()}>
              {i.skutopia_url ? <a href={i.skutopia_url} target="_blank" rel="noreferrer" style={{ color: '#7c3aed', fontSize: 11 }}>open ↗</a> : <span style={{ color: '#d4d4d8' }}>—</span>}
            </td>
            <td style={TD} onClick={e => e.stopPropagation()}>
              {trk.length ? <a href={trk[0].tracking_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontSize: 11 }}>{trk[0].carrier || 'track'}{trk.length > 1 ? ` +${trk.length - 1}` : ''} ↗</a> : <span style={{ color: '#d4d4d8' }}>—</span>}
            </td>
          </tr>
        );
      })}
    </>
  );
}
